import { useMemo, useState } from "react";
import {
  GiScrollUnfurled,
  GiSkullCrossedBones,
  GiSwordWound,
} from "react-icons/gi";
import {
  BattleEventEntry,
  BattleLogEntry,
  BattleOutcome,
  BattleRoundEntry,
  CasualtyBreakdownEntry,
} from "../types/marches";
import { formatNumber } from "../lib/format";
import { useUnitCatalog } from "../api/army";
import { UnitClassIcon } from "../types/army";
import { CurrencyValue } from "./CurrencyIcon";

interface Props {
  battle: BattleLogEntry;
  selfId: string | null;
  onBack: () => void;
}

export function BattleDetail({ battle, selfId, onBack }: Props) {
  const units = useUnitCatalog();
  const unitsById = useMemo(() => {
    const m = new Map<string, NonNullable<typeof units.data>[number]>();
    units.data?.forEach((u) => m.set(u.id, u));
    return m;
  }, [units.data]);

  const wasAttacker = selfId != null && battle.attackerId === selfId;
  const won =
    (battle.outcome === BattleOutcome.TeamA && wasAttacker) ||
    (battle.outcome === BattleOutcome.TeamB && !wasAttacker);
  const isDraw = battle.outcome === BattleOutcome.Draw;
  const isPve = battle.campId != null;

  const r = battle.resolution;
  const myBreakdown = wasAttacker ? r.attackerBreakdown : r.defenderBreakdown;
  const enemyBreakdown = wasAttacker ? r.defenderBreakdown : r.attackerBreakdown;
  const myUnitIds = wasAttacker ? r.attackerUnitIds : r.defenderUnitIds;
  const enemyUnitIds = wasAttacker ? r.defenderUnitIds : r.attackerUnitIds;
  const myRemainingPower = wasAttacker ? r.report.remainingPowerA : r.report.remainingPowerB;
  const enemyRemainingPower = wasAttacker ? r.report.remainingPowerB : r.report.remainingPowerA;

  const labelStack = (
    ids: string[] | null,
    stackIdx: number | null | undefined,
  ): string => {
    if (stackIdx == null) return "";
    const unitId = ids?.[stackIdx];
    if (!unitId) return `#${stackIdx}`;
    const u = unitsById.get(unitId);
    if (!u) return `#${stackIdx}`;
    const icon = UnitClassIcon[u.cls] ?? "";
    return `${icon} ${u.name}`.trim();
  };

  const date = new Date(battle.resolvedAt).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const verdict = isDraw ? "Ничья" : won ? "Победа" : "Поражение";
  const verdictColor = isDraw
    ? "text-tg-hint"
    : won
    ? "text-tg-accent"
    : "text-tg-destructive";

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div>
          <div className="font-medium">
            {isPve ? "PvE атака" : wasAttacker ? "Моя атака" : "Атака на меня"}
          </div>
          <div className="text-xs text-tg-hint">{date}</div>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-3 pb-3">
        {!isDraw && (
          <div
            className="relative h-44 w-full overflow-hidden rounded-xl"
            style={{ boxShadow: "0 0 30px var(--element-glow)" }}
          >
            <img
              src={`/icons/battle/${won ? "victory" : "defeat"}.jpg`}
              alt={verdict}
              className="absolute inset-0 h-full w-full object-cover"
              loading="eager"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7) 100%)",
              }}
            />
            <div className="absolute inset-x-0 bottom-0 p-3">
              <div className={`text-3xl font-bold drop-shadow-lg ${verdictColor}`}>
                {verdict}
              </div>
              <div className="text-xs text-tg-hint drop-shadow">
                {r.report.rounds} раундов · Power после {formatNumber(myRemainingPower)} /{" "}
                {formatNumber(enemyRemainingPower)}
              </div>
            </div>
          </div>
        )}
        {isDraw && (
          <div className="rounded-xl bg-tg-secondaryBg p-3">
            <div className="flex items-baseline justify-between">
              <div className="text-xs text-tg-hint">Итог</div>
              <div className={`text-lg font-semibold ${verdictColor}`}>
                {verdict}
              </div>
            </div>
            <div className="mt-2 text-xs text-tg-hint">
              {r.report.rounds} раундов · Power после {formatNumber(myRemainingPower)} /{" "}
              {formatNumber(enemyRemainingPower)}
            </div>
          </div>
        )}

        {wasAttacker && won && (
          <Section title="Лут">
            <div className="rounded-xl bg-tg-secondaryBg p-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <CurrencyValue kind="gold" value={formatNumber(r.lootForAttacker.gold)} size={16} />
                <CurrencyValue kind="wood" value={formatNumber(r.lootForAttacker.wood)} size={16} />
                <CurrencyValue kind="stone" value={formatNumber(r.lootForAttacker.stone)} size={16} />
                <CurrencyValue kind="mana" value={formatNumber(r.lootForAttacker.mana)} size={16} />
                {r.heroXpForAttacker > 0 && (
                  <div className="col-span-2 text-tg-accent">
                    <span className="inline-flex items-center gap-1"><GiScrollUnfurled size={12} /> +{formatNumber(r.heroXpForAttacker)} Hero XP</span>
                  </div>
                )}
              </div>
            </div>
          </Section>
        )}

        <Section title={<span className="inline-flex items-center gap-1.5"><GiSwordWound size={14} /> Мои потери</span>}>
          <CasualtyTable
            breakdown={myBreakdown}
            isAttackerSide={wasAttacker}
            labelStack={(idx) => labelStack(myUnitIds, idx)}
          />
        </Section>

        <Section title={<span className="inline-flex items-center gap-1.5"><GiSkullCrossedBones size={14} /> Потери противника</span>}>
          <CasualtyTable
            breakdown={enemyBreakdown}
            isAttackerSide={!wasAttacker}
            labelStack={(idx) => labelStack(enemyUnitIds, idx)}
          />
        </Section>

        {r.report.roundLog && r.report.roundLog.length > 0 ? (
          <Section title={<span className="inline-flex items-center gap-1.5"><GiScrollUnfurled size={14} /> По раундам ({r.report.roundLog.length})</span>}>
            <RoundsView
              rounds={r.report.roundLog}
              wasAttacker={wasAttacker}
              labelMine={(idx) => labelStack(myUnitIds, idx)}
              labelEnemy={(idx) => labelStack(enemyUnitIds, idx)}
            />
          </Section>
        ) : (
          <div className="rounded-xl bg-tg-secondaryBg p-3 text-xs text-tg-hint">
            Детальный лог по раундам недоступен для этого боя.
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">{title}</div>
      {children}
    </section>
  );
}

function CasualtyTable({
  breakdown,
  isAttackerSide,
  labelStack,
}: {
  breakdown: CasualtyBreakdownEntry[];
  isAttackerSide: boolean;
  labelStack: (stackIdx: number) => string;
}) {
  if (breakdown.length === 0) {
    return (
      <div className="rounded-xl bg-tg-secondaryBg p-3 text-sm text-tg-hint">
        Без потерь.
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-tg-secondaryBg p-3 text-xs">
      {breakdown.map((b) => (
        <div
          key={b.stackIndex}
          className="border-t border-tg-bg first:border-t-0 py-1.5"
        >
          <div className="mb-0.5 text-sm font-medium">{labelStack(b.stackIndex)}</div>
          <div className="grid grid-cols-5 gap-1 tabular-nums">
            <Cell label="Было" value={b.initialCount} className="" />
            <Cell label="Жив." value={b.survivors} className="" />
            <Cell label="Лёгк." value={b.lightWounded} className="text-yellow-600 dark:text-yellow-300" />
            <Cell label="Тяж." value={b.heavyWounded} className="text-orange-600 dark:text-orange-300" />
            <Cell label="†" value={b.dead} className="text-tg-destructive" />
          </div>
        </div>
      ))}
      <div className="mt-2 text-tg-hint">
        {isAttackerSide
          ? "Лёгк. → гарнизон сразу · Тяж. → Госпиталь · † навсегда"
          : "Лёгк. → гарнизон сразу · Тяж. → Госпиталь · 70% † → Храм возрождения"}
      </div>
    </div>
  );
}

function Cell({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className: string;
}) {
  return (
    <div className="text-center">
      <div className="text-tg-hint">{label}</div>
      <div className={className}>{formatNumber(value)}</div>
    </div>
  );
}

function RoundsView({
  rounds,
  wasAttacker,
  labelMine,
  labelEnemy,
}: {
  rounds: BattleRoundEntry[];
  wasAttacker: boolean;
  labelMine: (stackIdx: number | null | undefined) => string;
  labelEnemy: (stackIdx: number | null | undefined) => string;
}) {
  const [openRound, setOpenRound] = useState<number | null>(rounds[0]?.number ?? null);
  return (
    <div className="space-y-1">
      {rounds.map((round) => {
        const isOpen = openRound === round.number;
        return (
          <div key={round.number} className="rounded-xl bg-tg-secondaryBg">
            <button
              type="button"
              onClick={() => setOpenRound(isOpen ? null : round.number)}
              className="flex w-full items-center justify-between p-3 text-left"
            >
              <div className="text-sm font-medium">Раунд {round.number}</div>
              <div className="text-xs text-tg-hint">
                {round.events.length} событий · {isOpen ? "▼" : "▶"}
              </div>
            </button>
            {isOpen && (
              <div className="space-y-0.5 border-t border-tg-bg px-3 py-2 text-xs">
                {round.events.map((ev, i) => (
                  <EventLine
                    key={i}
                    ev={ev}
                    wasAttacker={wasAttacker}
                    labelMine={labelMine}
                    labelEnemy={labelEnemy}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EventLine({
  ev,
  wasAttacker,
  labelMine,
  labelEnemy,
}: {
  ev: BattleEventEntry;
  wasAttacker: boolean;
  labelMine: (idx: number | null | undefined) => string;
  labelEnemy: (idx: number | null | undefined) => string;
}) {
  // team 0 = TeamA = attacker; team 1 = TeamB = defender.
  const sourceIsMine = (ev.team === 0) === wasAttacker;
  const sideIcon = sourceIsMine ? "🟢" : "🔴";

  // Source belongs to the acting team; target belongs to the other team
  // (except for heals/buffs, which target own team).
  const targetIsMine =
    ev.kind === 4 || ev.kind === 5 ? sourceIsMine : !sourceIsMine;
  const srcLabel = ev.sourceStackIndex != null
    ? sourceIsMine
      ? labelMine(ev.sourceStackIndex)
      : labelEnemy(ev.sourceStackIndex)
    : "";
  const tgtLabel = ev.targetStackIndex != null
    ? targetIsMine
      ? labelMine(ev.targetStackIndex)
      : labelEnemy(ev.targetStackIndex)
    : "";

  const heroPrefix = ev.heroIndex != null ? `🦸#${ev.heroIndex} ` : "";

  switch (ev.kind) {
    case 0: // Hit
      return (
        <div>
          {sideIcon} {heroPrefix}
          <b>{srcLabel}</b> ⚔️ {tgtLabel}
          <span className="ml-1 tabular-nums text-tg-hint">−{formatNumber(ev.amount)}</span>
        </div>
      );
    case 1: // Crit
      return (
        <div>
          {sideIcon} {heroPrefix}
          <b>{srcLabel}</b> 💥 КРИТ {tgtLabel}
          <span className="ml-1 tabular-nums text-tg-hint">−{formatNumber(ev.amount)}</span>
        </div>
      );
    case 2: // MageAoeHit
      return (
        <div>
          {sideIcon} {heroPrefix}
          <b>{srcLabel}</b> 🌀 AoE {tgtLabel}
          <span className="ml-1 tabular-nums text-tg-hint">−{formatNumber(ev.amount)}</span>
        </div>
      );
    case 3: // SkillDamage
      return (
        <div>
          {sideIcon} {heroPrefix}🔥 скилл-урон {tgtLabel}
          <span className="ml-1 tabular-nums text-tg-hint">−{formatNumber(ev.amount)}</span>
        </div>
      );
    case 4: // SkillHeal
      return (
        <div>
          {sideIcon} {heroPrefix}🩹 хил {tgtLabel}
          <span className="ml-1 tabular-nums text-tg-accent">+{formatNumber(ev.amount)}</span>
        </div>
      );
    case 5: // SkillBuff
      return (
        <div>
          {sideIcon} {heroPrefix}✨ бафф
          <span className="ml-1 tabular-nums text-tg-accent">+{Math.round(ev.amount)}%</span>
        </div>
      );
    default:
      return (
        <div>
          {sideIcon} событие #{ev.kind}
        </div>
      );
  }
}
